import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, catchError, of, switchMap, throwError, EMPTY, merge } from 'rxjs';
import { IndexedDBService } from '@libs/shared-services';
import { scheduleDBKeys, SKIP_CACHE } from '@libs/utils';
import { OperatoryAppointmentsResponseV2 } from '@ffd/api-client';

export interface ScheduleData {
  appointments: OperatoryAppointmentsResponseV2;
  createdAt: Date;
}

@Injectable({ providedIn: 'root' })
export class ScheduleInterceptorService implements HttpInterceptor {
  private readonly indexedDbService = inject(IndexedDBService);

  private db: IDBDatabase | undefined;

  constructor() {
    this.indexedDbService.dbOpen(scheduleDBKeys.dbName, scheduleDBKeys.version, scheduleDBKeys.objectStoreName).subscribe((db) => {
      this.db = db;
    });
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<OperatoryAppointmentsResponseV2>> {
    if (req.url.includes('/appointments') && req.method === 'GET' && !req.context.get(SKIP_CACHE) && this.db) {
      const url = req.urlWithParams;
      const siteIdMatch = url.match(/sites\/([^/]+)/);
      const siteId = siteIdMatch ? siteIdMatch[1] : null;
      const scheduleFromDate = req.params.get('scheduleFromDate');

      // Prefixed with the API/schema version, so appointments cached with numeric ids (v1, or v2 before
      // appointment/status ids became strings) are not served
      const appointmentsResponseId = `v2s_${scheduleFromDate}_${siteId}`;
      if (appointmentsResponseId) {
        return merge(
          this.getCachedAppointments(appointmentsResponseId).pipe(
            catchError((error) => {
              return EMPTY;
            }),
          ),
          this.handleRequestAndStoreInCache(appointmentsResponseId, next, req),
        );
      }
    }
    return next.handle(req);
  }

  private readonly getCachedAppointments = (appointmentsResponseId: string) => {
    const readOS = this.db!.transaction([scheduleDBKeys.objectStoreName], 'readonly').objectStore(scheduleDBKeys.objectStoreName);
    return this.indexedDbService.dbGet<ScheduleData>(readOS, appointmentsResponseId).pipe(
      switchMap((data) => {
        if (data) {
          return of(
            new HttpResponse({
              body: data.appointments,
              status: 200,
            }),
          );
        }
        return EMPTY;
      }),
    );
  };

  private readonly handleRequestAndStoreInCache = (
    appointmentsResponseId: string,
    next: HttpHandler,
    req: HttpRequest<unknown>,
  ): Observable<HttpEvent<OperatoryAppointmentsResponseV2>> => {
    return next.handle(req).pipe(
      catchError(() => {
        return throwError(() => 'Error fetching appointments');
      }),
      switchMap((response) => {
        if (response instanceof HttpResponse) {
          const writeOs = this.db!.transaction([scheduleDBKeys.objectStoreName], 'readwrite').objectStore(scheduleDBKeys.objectStoreName);
          return this.indexedDbService
            .dbPut<ScheduleData>(writeOs, appointmentsResponseId, {
              appointments: response.body,
              createdAt: new Date(),
            })
            .pipe(
              switchMap(async () => {
                await this.indexedDbService.dbHouseKeeping(writeOs);
                return response;
              }),
            );
        }
        return of(response);
      }),
    );
  };
}
